import { useMemo } from 'react';
import { pick } from 'ramda';
import { TFormField } from 'js/components/common/Form/types';
import { TValidationMethodConfig } from 'js/utils/Validation/types';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import * as helpers from '../helpers';
import { useDynamicReducerSelector } from '../store';
import { useLimitsValidationConfig } from '../components/Limits/hooks';
import { useDepositLimitsValidationConfig } from '../components/DepositLimits/hooks';

export const useValidationConfig = () => {
    const { hiddenByRulesFields, hiddenFields, fields } = useDynamicReducerSelector(pick(['hiddenByRulesFields', 'hiddenFields', 'fields']));

    const visibleEditableFields = useMemo(() => fields
        .filter((field: TFormField) => !field.hideFromUser
            && !field.readOnly
            && (!hiddenByRulesFields || !hiddenByRulesFields.includes(field.name))
        ), [fields, hiddenByRulesFields]);

    const fieldsToValidate = useMemo(() => visibleEditableFields
        .filter((field: TFormField) => field.validation
            && (!hiddenFields || !hiddenFields.includes(field.name))), [hiddenFields, visibleEditableFields]);
    const innerFieldsToValidate = useMemo(() => visibleEditableFields
        .flatMap(field => 'innerFields' in field ? Object.values(field.innerFields) : [])
        .filter((field: TFormField) => field.validation
            && field.active
            && !field.hideFromUser
            && !field.readOnly
            && (!hiddenFields || !hiddenFields.includes(field.name))
        ), [visibleEditableFields, hiddenFields]);

    // region Limits
    const depositLimitsField = fieldsToValidate.find(field => field.type === FORM_FIELD_TYPE.DEPOSIT_LIMITS);
    const depositLimitsValidationConfig = useDepositLimitsValidationConfig(depositLimitsField);
    const betLimitsField = fieldsToValidate.find(field => field.type === FORM_FIELD_TYPE.BET_LIMITS);
    const betLimitsValidationConfig = useLimitsValidationConfig(betLimitsField);
    const lossLimitsField = fieldsToValidate.find(field => field.type === FORM_FIELD_TYPE.LOSS_LIMITS);
    const lossLimitsValidationConfig = useLimitsValidationConfig(lossLimitsField);
    // endregion

    return useMemo(() => [...fieldsToValidate, ...innerFieldsToValidate].reduce((config, field) => ({
        ...config,
        ...helpers.getFieldValidations(field),
        ...depositLimitsValidationConfig,
        ...betLimitsValidationConfig,
        ...lossLimitsValidationConfig
    }), {} as Record<string, TValidationMethodConfig[]>), [
        fieldsToValidate, innerFieldsToValidate,
        depositLimitsValidationConfig, betLimitsValidationConfig, lossLimitsValidationConfig
    ]);
};
